import { Action, createReducer, on } from "@ngrx/store";

import { CustomerPartyDTO } from "src/app/models/Party/customerPartyDTO";
import { SupplierPartyDTO } from "src/app/models/Party/supplierPartyDTO";
import { EmployeePartyDTO } from "src/app/models/Party/employeePartyDTO";
import { PaginationRequest } from "src/app/models/common/paginationRequest";
import { CategoryDTO } from "src/app/models/Categories/categoryDTO";
import { CategoryElementDTO } from "src/app/models/Categories/categoryElementDTO";
import { PropertyDTO } from "src/app/models/Properties/propertyDTO";
import { CardPlafondDTO } from "src/app/models/Cards/cardPlafondDTO";
import { CardSystemDTO } from "src/app/models/Cards/cardsSystemDTO";
import { CardLayoutDTO } from "src/app/models/Cards/cardLayoutDTO";
import { CardStatusDTO } from "src/app/models/Cards/cardStatusDTO";
import { ColumnSorterInfo } from "src/app/models/custom-table/custom-table.model";

import * as CardActionTypes from "../../actions/cards/cards.actions";
import * as PartiesActionTypes from "../../actions/parties/parties.actions";

export interface CardsState {
  created: boolean;
  card: any;
  cardPlafond: CardPlafondDTO;
  cardSystems: CardSystemDTO[];
  cardLayouts: CardLayoutDTO[];
  cardStatus: CardStatusDTO[];
  customerParty: CustomerPartyDTO;
  supplierParty: SupplierPartyDTO;
  employeeParty: EmployeePartyDTO;
  isLoading: boolean;
  error: Error;
  totalRecords: number;
  pageReq: PaginationRequest;
  categories: CategoryDTO[];
  optionalCategories: CategoryDTO[];
  categoryElements: CategoryElementDTO[];
  properties: PropertyDTO[];
  optionalProperties: PropertyDTO[];
  customerPartyList: any; // CustomCustomerDTO[];
  employeePartyList: any; // CustomEmployeeDTO[];
  cardList: any;
  contractList: any;
  vehiclesList: any;
  categoriesList: CategoryDTO[];
  propertiesList: PropertyDTO[];
  limitIntervals: any[];
  limitMeasurements: any[];
  transactions: any[];
  totalCards?: number;
  cardExcelList?: any[];
  cardTableConfig: ColumnSorterInfo[],
}

export const initialState: CardsState = {
  created: false,
  card: null,
  cardPlafond: null,
  cardSystems: null,
  cardLayouts: null,
  cardStatus: null,
  customerParty: null,
  supplierParty: null,
  employeeParty: null,
  isLoading: false,
  error: null,
  totalRecords: 0,
  pageReq: null,
  categories: null,
  optionalCategories: null,
  categoryElements: null,
  properties: null,
  optionalProperties: null,
  customerPartyList: null, // CustomCustomerDTO[];
  employeePartyList: null, // CustomEmployeeDTO[];
  cardList: null,
  contractList: null,
  vehiclesList: null,
  categoriesList: null,
  propertiesList: null,
  limitIntervals: null,
  limitMeasurements: null,
  transactions: null,
  cardExcelList: null,
  totalCards: null,
  cardTableConfig: null,
};

const cardsReducer = createReducer(
  initialState,
  on(CardActionTypes.resetCardsState, () => initialState),
  on(
    CardActionTypes.getCardSystems,
    (state, { pageReq }) =>
      ({
        ...state,
        error: null,
        pageReq: pageReq,
      } as CardsState)
  ),
  on(CardActionTypes.loadCardPlafond, (state, { response }) => {
    return {
      ...state,
      cardPlafond: (response as any).result
    }
  }),
  on(CardActionTypes.loadCardSystems, (state, { response }) => {
    let currentOffset = state.pageReq ? state.pageReq.offset : 0;
    let totalRecords = Math.max(
      currentOffset + (response as any)?.result?.size,
      state.totalRecords
    );
    return {
      ...state,
      cardSystems: (response as any)?.result.items || [],
      error: null,
      totalRecords: totalRecords,
    };
  }),
  on(CardActionTypes.setLoadingStartState, (state) => {
    return {
      ...state,
      isLoading: true,
      error: null,
    };
  }),
  on(CardActionTypes.setLoadingEndState, (state) => {
    return {
      ...state,
      isLoading: false,
      error: null,
    };
  }),
  on(
    CardActionTypes.getCardLayout,
    (state, { pageReq }) =>
      ({
        ...state,
        error: null,
        pageReq: pageReq,
      } as CardsState)
  ),
  on(CardActionTypes.loadCardLayout, (state, { response }) => {
    let currentOffset = state.pageReq ? state.pageReq.offset : 0;
    let totalRecords = Math.max(
      currentOffset + (response as any)?.result?.size,
      state.totalRecords
    );
    return {
      ...state,
      cardLayouts: (response as any)?.result.items || [],
      error: null,
      totalRecords: totalRecords,
    };
  }),
  on(
    CardActionTypes.getCardContractList,
    (state, { customerCode }) =>
      ({
        ...state,
        error: null,
        action: customerCode,
      } as CardsState)
  ),
  on(CardActionTypes.loadCardContractList, (state, { response }) => {
    let currentOffset = state.pageReq ? state.pageReq.offset : 0;
    let totalRecords = Math.max(
      currentOffset + (response as any)?.result?.size,
      state.totalRecords
    );
    return {
      ...state,
      contractList: (response as any)?.result.items || [],
      error: null,
      totalRecords: totalRecords,
    };
  }),
  on(
    CardActionTypes.getCardEmployeePartyList,
    (state, { customerCode }) =>
      ({
        ...state,
        error: null,
        action: customerCode,
      } as CardsState)
  ),
  on(CardActionTypes.loadCardEmployeePartyList, (state, { response }) => {
    let currentOffset = state.pageReq ? state.pageReq.offset : 0;
    let totalRecords = Math.max(
      currentOffset + (response as any)?.result?.size,
      state.totalRecords
    );
    return {
      ...state,
      employeePartyList: (response as any)?.result.items || [],
      error: null,
      totalRecords: totalRecords,
    };
  }),
  on(
    CardActionTypes.getCardVehiclesList,
    (state, { customerCode }) =>
      ({
        ...state,
        error: null,
        action: customerCode,
      } as CardsState)
  ),
  on(CardActionTypes.loadCardVehiclesList, (state, { response }) => {
    let currentOffset = state.pageReq ? state.pageReq.offset : 0;
    let totalRecords = Math.max(
      currentOffset + (response as any)?.result?.size,
      state.totalRecords
    );
    return {
      ...state,
      vehiclesList: (response as any)?.result.items || [],
      error: null,
      totalRecords: totalRecords,
    };
  }),
  on(CardActionTypes.getCardFilteredList, (state, { options }) => {
    return {
      ...state,
      options: options,
      isLoading: true,
    };
  }),
  on(CardActionTypes.loadCardFilteredList, (state, { response }) => {
    return {
      ...state,
      cardList: (response as any).result?.items
        ? (response as any).result?.items
        : undefined,
      error: null,
      isLoading: false,
      totalCards: (response as any)?.result?.size
        ? (response as any)?.result?.size
        : 0,
    };
  }),
  on(CardActionTypes.getCardExcelList, (state, { options }) => {
    return {
      ...state,
      options: options,
      isLoading: true,
    };
  }),
  on(CardActionTypes.loadCardExcelList, (state, { response }) => {
    return {
      ...state,
      cardExcelList: (response as any).result?.items
        ? (response as any).result?.items
        : undefined,
      error: null,
      isLoading: false,
    };
  }),
  on(CardActionTypes.getCardTableConfig, (state) => {
    return {
      ...state,
      isLoading: true,
    }
  }),
  on(CardActionTypes.loadCardTableConfig, (state, { response }) => {
    return {
      ...state,
      cardTableConfig: (response as any)?.result?.value?.tableColumnInfo ?? undefined,
    };
  }),
  on(CardActionTypes.saveCardTableConfig, (state) => {
    return {
      ...state,
      isLoading: true,
    };
  }),
  on(
    CardActionTypes.getCustomerCardList,
    (state, { container, options }) =>
      ({
        ...state,
        error: null,
        ontainer: container,
        options: options,
      } as CardsState)
  ),
  on(CardActionTypes.loadCustomerCardList, (state, { response }) => {
    let currentOffset = state.pageReq ? state.pageReq.offset : 0;
    let totalRecords = Math.max(
      currentOffset + (response as any)?.result?.size,
      state?.totalRecords
    );
    return {
      ...state,
      cardList: (response as any)?.result
        ? (response as any)?.result
        : undefined,
      error: null,
      totalRecords: totalRecords,
    };
  }),
  on(
    CardActionTypes.getCard,
    (state, { code }) =>
      ({
        ...state,
        isLoading: true,
        error: null,
        created: false,
        action: code,
      } as CardsState)
  ),
  on(CardActionTypes.loadCard, (state, { response }) => {
    return {
      ...state,
      card: (response as any)?.result ? (response as any).result : undefined,
      error: null,
      isLoading: false,
    };
  }),
  on(CardActionTypes.resetCard, (state) => {
    return {
      ...state,
      card: null,
      error: null,
    };
  }),
  on(
    CardActionTypes.getLimitIntervals,
    (state, { pageReq }) =>
      ({
        ...state,
        error: null,
        pageReq: pageReq,
      } as CardsState)
  ),
  on(CardActionTypes.loadLimitIntervals, (state, { response }) => {
    return {
      ...state,
      limitIntervals: (response as any).result?.items || [],
      error: null,
    };
  }),
  on(
    CardActionTypes.getLimitMeasurements,
    (state, { pageReq }) =>
      ({
        ...state,
        error: null,
        pageReq: pageReq,
      } as CardsState)
  ),
  on(CardActionTypes.loadLimitMeasurements, (state, { response }) => {
    return {
      ...state,
      limitMeasurements: (response as any)?.result?.items || [],
      error: null,
    };
  }),
  on(CardActionTypes.loadCardTransactionList, (state, { response }) => {
    return {
      ...state,
      transactions: (response as any)?.result || [],
      error: null,
    };
  }),
  on(CardActionTypes.resetCreation, (state) => {
    return {
      ...state,
      created: true,
    };
  }),
  on(
    CardActionTypes.createCard,
    CardActionTypes.updateCard,
    CardActionTypes.resetCardPin,
    CardActionTypes.resetCardLimit,
    (state) => {
      return {
        ...state,
        isLoading: true,
      };
   }
  ),
);

export function reducer(state: CardsState | undefined, action: Action) {
  return cardsReducer(state, action);
}
